// دیالوگ‌های SCR-STAFF-200: ایجاد و ویرایش دسته (US-CAT-001، علامت حساس AC-CAT-001.3) و تعریف خدمت تازه (US-CAT-002).
import { zodResolver } from '@hookform/resolvers/zod';
import Stack from '@mui/material/Stack';
import { unwrap, type CategoryView, type ServiceType, type ServiceUnit } from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  FormDialog,
  SelectRHF,
  TextFieldRHF,
  zRequiredText,
} from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm, type Control, type FieldValues, type Path } from 'react-hook-form';
import { useDetailNavigate } from '../../layout/detailNavigation';
import { z } from 'zod';
import { categoryPath, catKeys, SERVICE_TYPES, SERVICE_UNITS, servicePath } from './common';
import { MinutesFieldRHF } from './MinutesField';

const CATEGORY_CODE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,31}$/;
export const SERVICE_CODE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,31}$/;

const categorySchema = z.object({
  code: z.string().regex(CATEGORY_CODE, { error: 'errors:validation.category_code' }),
  name: zRequiredText(200),
  parentId: z.string(),
  isSensitive: z.boolean(),
});
type CategoryForm = z.input<typeof categorySchema>;

/** ایجاد (category = null) یا ویرایش دسته. */
export function CategoryDialog({
  category,
  defaultParentId,
  categories,
  onClose,
}: {
  category: CategoryView | null;
  defaultParentId: string | null;
  categories: readonly CategoryView[];
  onClose: () => void;
}) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const creating = category === null;

  const values = useMemo<CategoryForm>(
    () => ({
      code: category?.code ?? '',
      name: category?.name ?? '',
      parentId: category?.parentId ?? defaultParentId ?? '',
      isSensitive: category?.isSensitive ?? false,
    }),
    [category, defaultParentId],
  );
  const { control, handleSubmit, formState, setError } = useForm<CategoryForm>({
    resolver: zodResolver(categorySchema),
    values,
  });

  // والد مجاز: عمق ≤ ۲ (فرزند ≤ ۳ — BR-CAT-001)، نه خود دسته.
  const parents = categories.filter((item) => Number(item.depth) < 3 && item.id !== category?.id);

  const save = useMutation({
    mutationFn: (form: CategoryForm) => {
      const body = {
        code: form.code.trim(),
        name: form.name,
        parentId: form.parentId || null,
        isSensitive: form.isSensitive,
        sortOrder: Number(category?.sortOrder ?? 0),
        rowVersion: creating ? null : Number(category.rowVersion),
      };
      return creating
        ? unwrap(
            tenantApi.POST('/api/v1/catalog/categories', {
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
          )
        : unwrap(
            tenantApi.PUT('/api/v1/catalog/categories/{id}', { params: { path: { id: category.id } }, body }),
          );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'parentId', 'isSensitive'],
        codeFields: { 'PHX-CAT-0102': 'code', 'PHX-CAT-0101': 'parentId', 'PHX-CAT-0104': 'parentId' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-200-CATEGORY"
      titleKey={creating ? 'cat:categories.create_title' : 'cat:categories.edit_title'}
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="cat:categories.form.code"
          required
          fullWidth
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'category-code' } }}
        />
        <TextFieldRHF
          control={control}
          name="name"
          labelKey="cat:categories.form.name"
          required
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': 'category-name' } }}
        />
      </Stack>
      <SelectRHF
        control={control}
        name="parentId"
        labelKey="cat:categories.form.parent"
        helperKey="cat:categories.form.parent_help"
        fullWidth
        options={[
          { value: '', labelKey: 'cat:categories.form.root' },
          ...parents.map((item) => ({
            value: item.id,
            label: categoryPath(categories, item.id).join(' / '),
          })),
        ]}
      />
      <CheckboxRHF
        control={control}
        name="isSensitive"
        labelKey="cat:categories.form.sensitive"
        helperKey="cat:categories.form.sensitive_help"
      />
      {!creating && category.inheritsSensitive && (
        <span data-testid="category-inherits-sensitive">{t('cat:categories.form.inherited_notice')}</span>
      )}
    </FormDialog>
  );
}

const minutes = (min: number) =>
  z
    .number({ error: 'common:validation.required' })
    .int({ error: 'errors:error.duration_invalid' })
    .min(min, { error: 'errors:error.duration_invalid' })
    .max(1440, { error: 'errors:error.duration_invalid' })
    .refine((value) => value % 5 === 0, { error: 'errors:error.duration_invalid' });

/** مدت (BR-CAT-010): اصلی ≥ ۵ و مضرب ۵؛ بافرها ≥ ۰ و مضرب ۵؛ مدت به ازای واحد اختیاری. */
export const durationSchema = z.object({
  durationMinutes: minutes(5),
  bufferBeforeMinutes: minutes(0),
  bufferAfterMinutes: minutes(0),
  perUnitMinutes: minutes(5).nullable(),
});

const serviceSchema = z.object({
  code: z.string().regex(SERVICE_CODE, { error: 'errors:validation.service_code' }),
  name: zRequiredText(200),
  shortName: z.string().max(30, { error: 'common:validation.too_long' }),
  categoryId: z.string().min(1, { error: 'common:validation.required' }),
  type: z.enum(SERVICE_TYPES as [ServiceType, ...ServiceType[]]),
  unit: z.enum(SERVICE_UNITS as [ServiceUnit, ...ServiceUnit[]]),
  duration: durationSchema,
});
type ServiceForm = z.input<typeof serviceSchema>;

/** تعریف خدمت تازه (Draft) و رفتن به جزئیات آن. */
export function ServiceCreateDialog({
  categories,
  defaultCategoryId,
  onClose,
}: {
  categories: readonly CategoryView[];
  defaultCategoryId: string | null;
  onClose: () => void;
}) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const openDetail = useDetailNavigate();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<ServiceForm>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      code: '',
      name: '',
      shortName: '',
      categoryId: defaultCategoryId ?? '',
      type: 'Visit',
      unit: 'Each',
      duration: { durationMinutes: 15, bufferBeforeMinutes: 0, bufferAfterMinutes: 0, perUnitMinutes: null },
    },
  });

  const create = useMutation({
    mutationFn: (form: ServiceForm) =>
      unwrap(
        tenantApi.POST('/api/v1/catalog/services', {
          body: {
            code: form.code.trim(),
            name: form.name,
            shortName: form.shortName.trim() || null,
            categoryId: form.categoryId,
            type: form.type,
            unit: form.unit,
            specialtyCodes: [],
            description: null,
            duration: {
              durationMinutes: form.duration.durationMinutes,
              bufferBeforeMinutes: form.duration.bufferBeforeMinutes,
              bufferAfterMinutes: form.duration.bufferAfterMinutes,
              perUnitMinutes: form.duration.perUnitMinutes ?? null,
            },
            rowVersion: null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (service) => {
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
      onClose();
      openDetail(servicePath(service.id));
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'shortName', 'categoryId', 'type', 'unit'],
        codeFields: {
          'PHX-CAT-0201': 'code',
          'PHX-CAT-0105': 'categoryId',
          'PHX-CAT-0301': 'duration.durationMinutes',
        },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-200-SERVICE"
      titleKey="cat:services.create_title"
      onSubmit={() => void handleSubmit((form) => create.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={create.isPending}
      submitError={submitError}
      maxWidth="md"
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="code"
          labelKey="cat:services.form.code"
          helperKey="cat:services.form.code_help"
          required
          fullWidth
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'service-code' } }}
        />
        <TextFieldRHF
          control={control}
          name="name"
          labelKey="cat:services.form.name"
          required
          fullWidth
          slotProps={{ htmlInput: { 'data-testid': 'service-name' } }}
        />
        <TextFieldRHF control={control} name="shortName" labelKey="cat:services.form.short_name" fullWidth />
      </Stack>
      <SelectRHF
        control={control}
        name="categoryId"
        labelKey="cat:services.form.category"
        required
        fullWidth
        options={categories.map((item) => ({
          value: item.id,
          label: categoryPath(categories, item.id).join(' / '),
        }))}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="type"
          labelKey="cat:services.form.type"
          required
          fullWidth
          options={SERVICE_TYPES.map((value) => ({ value, labelKey: `cat:type.${value}` }))}
        />
        <SelectRHF
          control={control}
          name="unit"
          labelKey="cat:services.form.unit"
          required
          fullWidth
          options={SERVICE_UNITS.map((value) => ({ value, labelKey: `cat:unit.${value}` }))}
        />
      </Stack>
      <DurationFields control={control} prefix="duration" />
    </FormDialog>
  );
}

/** فیلدهای مدت (US-CAT-010). */
export function DurationFields<T extends FieldValues>({
  control,
  prefix,
  disabled,
}: {
  control: Control<T>;
  prefix: string;
  disabled?: boolean;
}) {
  const name = (field: string) => `${prefix}.${field}` as Path<T>;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <MinutesFieldRHF
        control={control}
        name={name('durationMinutes')}
        labelKey="cat:duration.main"
        required
        disabled={disabled}
        testId="duration-main"
      />
      <MinutesFieldRHF
        control={control}
        name={name('bufferBeforeMinutes')}
        labelKey="cat:duration.buffer_before"
        disabled={disabled}
        testId="duration-before"
      />
      <MinutesFieldRHF
        control={control}
        name={name('bufferAfterMinutes')}
        labelKey="cat:duration.buffer_after"
        disabled={disabled}
        testId="duration-after"
      />
      <MinutesFieldRHF
        control={control}
        name={name('perUnitMinutes')}
        labelKey="cat:duration.per_unit"
        helperKey="cat:duration.per_unit_help"
        nullable
        disabled={disabled}
        testId="duration-per-unit"
      />
    </Stack>
  );
}
